import { HiCalendar } from "react-icons/hi2";

import { compareDates } from "@/lib/compareDates";
import { RowData } from "@tanstack/react-table";

import { FilterByDateAppliedFilterView } from "../datatable-filters/filter-by-date-applied-filter-view";
import { FilterByDateConfigurationView } from "../datatable-filters/filter-by-date-configuration-view";
import { FilterByDateDisplayView } from "../datatable-filters/filter-by-date-display-view";
import { filterByDateWithUnknownPayload } from "../datatable-filters/functions/filter-by-date";
import { DatatableCellDate } from "../datatable-template-cells/datatable-cell-date";
import { DatatableColumnHeaderCell } from "../datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow } from "../datatable-types";

interface DatatableColumnDateProps<TItem extends RowData> {
  columnId: string;
  orderValue: number;
  displayValue: string;
  getDateField: (item: TItem) => Date | null | undefined;
}

export const DatatableColumnDate = <TItem extends RowData>({
  columnId,
  orderValue,
  displayValue,
  getDateField,
}: DatatableColumnDateProps<TItem>): DatatableColumn<TItem> => {
  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue, // prefix with 100, 200, 300 to assign to sections
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId, // key needed to enable sorting / filtering
        enableHiding: true,
        enableSorting: true,
        enableColumnFilter: true,
        size: 1, // set to 1 instead to 0 for it to work in Safari
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          const dateA = getDateField(rowA.original);
          const dateB = getDateField(rowB.original);

          if (!dateA || !dateB) {
            return 0;
          }

          if (!dateA) {
            return 1;
          }

          if (!dateB) {
            return -1;
          }

          return compareDates(dateA, dateB);
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            return true;
          }

          const maybeDate = getDateField(row.original);

          return filterByDateWithUnknownPayload(value, maybeDate ? maybeDate.toISOString() : null);
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className={`text-xs font-normal`}>
            {displayValue}
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          const maybeDate = getDateField(row.original);

          return (
            <DatatableCellDate
              row={row}
              date={maybeDate ? maybeDate.toISOString() : null}
              icon={<HiCalendar className="h-3.5 w-3.5 text-foreground/60" />}
            />
          );
        },
      };
    },
    renderDisplayView: () => <FilterByDateDisplayView displayValue={displayValue} />,
    getFilterConfigurationViewType: () => "modal",
    renderFilterConfigurationView: (onFinished: () => void) => (
      <FilterByDateConfigurationView
        column={{ id: columnId }}
        onFinished={onFinished}
        inputPlaceholder={"Geben Sie ein Datum ein ..."}
      />
    ),
    renderAppliedFilterView: () => (
      <FilterByDateAppliedFilterView
        column={{ id: columnId }}
        displayValue={displayValue}
        inputPlaceholder={"Geben Sie ein Datum ein ..."}
      />
    ),
  };
};
